import { useSuspenseQuery } from '@tanstack/react-query'
import { type LoaderFunctionArgs, type RouterContextProvider, useLoaderData } from 'react-router'

import { clientContext } from 'apps/employer_dashboard/router_contexts'
import { pageErrorData } from 'apps/employer_dashboard/utils/errors'
import DeductionsPage from 'components/deductions/DeductionsPage'
import { MissingScheduleForAutomationHeaderBanner } from 'components/payroll/MissingScheduleForAutomationHeaderBanner'
import { UnassignedEmployeesHeaderBanner } from 'components/payroll/UnassignedEmployeesHeaderBanner'
import { UnverifiedSchedulesHeaderBanner } from 'components/payroll/UnverifiedSchedulesHeaderBanner'
import { getGetPayrollFeaturesQueryKey, getGetPayrollManageConfigQueryKey, getPayrollFeatures, getPayrollManageConfig } from 'utils/web_api'

const payrollConfigQuery = {
  queryKey: getGetPayrollManageConfigQueryKey(),
  queryFn: getPayrollManageConfig,
}

const payrollFeaturesQuery = {
  queryKey: getGetPayrollFeaturesQueryKey(),
  queryFn: getPayrollFeatures,
}

export async function loader ({ context }: LoaderFunctionArgs<RouterContextProvider>) {
  const queryClient = context.get(clientContext)

  try {
    const payrollConfig = queryClient?.ensureQueryData(payrollConfigQuery)
    const payrollFeatures = queryClient?.ensureQueryData(payrollFeaturesQuery)

    const [payrollConfigData, payrollFeaturesData] = await Promise.all([payrollConfig, payrollFeatures])

    return { payrollConfigData, payrollFeaturesData }
  } catch (error) {
    throw pageErrorData(error, {
      heading: 'We could not load the deductions page',
      message: 'Something unexpected happened. We have been notified and are looking into it.'
    })
  }
}

export function PayrollDeductionsPage () {
  const {
    payrollConfigData: initialPayrollConfigData,
    payrollFeaturesData: initialPayrollFeaturesData
  } = useLoaderData<typeof loader>()

  const { data: payrollConfigData } = useSuspenseQuery({
    ...payrollConfigQuery,
    initialData: initialPayrollConfigData
  })

  const { data: payrollFeaturesData } = useSuspenseQuery({
    ...payrollFeaturesQuery,
    initialData: initialPayrollFeaturesData
  })

  const { reporting, banners } = payrollFeaturesData

  // TODO: Update the `<DeductionsPage />` component into this page
  // component after the ER dashboard migration is complete.

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex justify-between items-center'>
        <div className='flex space-x-2 items-center'>
          <h1 className='heading-lg'>
            Payroll Deductions
          </h1>
        </div>
      </div>

      {banners.has_unassigned_employees && <UnassignedEmployeesHeaderBanner showCta />}
      {banners.has_unverified_schedules && <UnverifiedSchedulesHeaderBanner showCta />}
      {banners.is_missing_schedule_for_automation && <MissingScheduleForAutomationHeaderBanner />}

      <hr />
      <div className='mb-6'>
        <DeductionsPage
          payrollProvider={payrollConfigData.provider_id}
          hasPayrollAutomation={payrollConfigData.has_automatic_payroll_deductions}
          humanizedPayrollProvider={payrollConfigData.provider_display_name}
          employerId={payrollConfigData.employer_id}
          reportingFeatures={reporting}
        />
      </div>
    </div>
  )
}
